import type { CampingSpot } from '../types';

/**
 * Danske plassadvarsler for visning.
 *
 * HVORFOR DENNE FILEN FINNES. Brukerbeslutning 16.08.2026: det danske
 * regelverket skal MERKE plasser, ikke slette dem. Målt nasjonalt kastet den
 * gamle kjeden 20 381 av 36 287 fysisk brukbare veiender på regler, og den
 * synlige delen (ikke-markvei) falt fra 4 272 til 750.
 *
 * ARBEIDSDELINGEN, som er verdt å holde fast på:
 *   `functions/lib/denmarkScan.js` eier KATALOGEN — hvilke nøkler finnes og
 *   hvor alvorlige de er. Alvorsgraden skrives på dokumentet som
 *   `warningSeverity`, så klienten aldri trenger å kjenne katalogen.
 *   `translations/*.json` eier TEKSTEN.
 *   Denne filen binder de to sammen og gjør ingen egen vurdering.
 *
 * `test/denmarkWarnings.test.ts` låser at hver katalognøkkel har tekst, så de
 * to ikke kan gli fra hverandre.
 */

export type DkWarningSeverity = 'hoy' | 'middels' | 'lav';

export interface DkWarningBadge {
  key: string;
  label: string;
  why: string;
  severity: DkWarningSeverity;
}

/**
 * Fallback-rangering for dokumenter skrevet før `warningSeverity` fantes.
 * Speiler `DK_SPOT_WARNINGS` i functions/lib/denmarkScan.js.
 */
const SEVERITY_BY_KEY: Record<string, DkWarningSeverity> = {
  bolig_svaert_naer: 'hoy',
  // `dyrket_mark` står MED VILJE ikke her: en plass på en registrert markblok
  // er en plass i en pløyd åker, og slettes i stedet for å merkes.
  // Brukeropprettede plasser slettes aldri automatisk (sourceLevel 4-vakten),
  // så en plass midt på en åker må si det selv.
  paa_dyrket_mark: 'hoy',
  p3natur: 'hoy',
  vildtreservat: 'hoy',
  byzone: 'hoy',
  sommerhus: 'hoy',
  bolig_naer: 'middels',
  // Markvei er hovedregelen i Danmark (97,6 % av plassene), og «inntil dyrket
  // mark» gjelder to av tre. Begge er middels: de forbyr ingenting, men de
  // krever at du vurderer stedet før du blir stående.
  markvei: 'middels',
  dyrket_mark_inntil: 'middels',
  bebyggelse: 'middels',
  fredet: 'middels',
  natura2000: 'middels',
  fugleres: 'middels',
  dige: 'middels',
  skovbyggelinje: 'lav',
  soebyggelinje: 'lav',
  aabyggelinje: 'lav',
};

const RANK: Record<DkWarningSeverity, number> = { hoy: 3, middels: 2, lav: 1 };

/** Fargene merket kan ha. Rød betyr «du bryter sannsynligvis noe». */
export const SEVERITY_CLASS: Record<DkWarningSeverity, string> = {
  hoy: 'text-red-300 border-red-500/40 bg-red-500/10',
  middels: 'text-amber-300 border-amber-500/40 bg-amber-500/10',
  lav: 'text-[var(--text-secondary)] border-stone-500/30 bg-stone-500/10',
};

export function severityOf(key: string): DkWarningSeverity | null {
  return SEVERITY_BY_KEY[key] || null;
}

/**
 * Den strengeste graden i settet. Bruker feltet fra dokumentet når det finnes,
 * ellers utledes det av nøklene — eldre dokumenter mangler feltet.
 */
export function spotWarningSeverity(spot: Partial<CampingSpot>): DkWarningSeverity | null {
  const lagret = (spot as { warningSeverity?: string }).warningSeverity;
  if (lagret && lagret in RANK) return lagret as DkWarningSeverity;
  let best: DkWarningSeverity | null = null;
  for (const k of (spot as { zoneWarnings?: string[] }).zoneWarnings || []) {
    const s = severityOf(k);
    if (s && (!best || RANK[s] > RANK[best])) best = s;
  }
  return best;
}

/**
 * Merkene som skal vises, strengest først.
 *
 * @param t i18n-oversetter. Ukjente nøkler hoppes over i stedet for å vise
 *          en rå nøkkel som «p3natur» til brukeren.
 */
export function dkWarningBadges(
  spot: Partial<CampingSpot>,
  t: (key: string, defaultValue?: string) => string,
): DkWarningBadge[] {
  const keys = (spot as { zoneWarnings?: string[] }).zoneWarnings;
  if (!Array.isArray(keys) || !keys.length) return [];
  const seen = new Set<string>();
  const ut: DkWarningBadge[] = [];
  for (const key of keys) {
    if (seen.has(key)) continue;
    seen.add(key);
    const severity = severityOf(key);
    if (!severity) continue;
    const label = t(`spot.dk_warning.${key}`, '');
    if (!label || label === `spot.dk_warning.${key}`) continue;
    ut.push({ key, label, why: t(`spot.dk_warning_why.${key}`, ''), severity });
  }
  return ut.sort((a, b) => RANK[b.severity] - RANK[a.severity]);
}
